import { createRouter, createWebHistory, type RouteLocationRaw, type Router } from "vue-router";

import { useUserStore } from "@/stores/modules/user";
import { useAuthStore } from "@/stores/modules/auth";
import { useUserPath } from "@/stores/modules/path";

import { ROUTER_WHITE_LIST } from "@/config";
import { filterRoutes, flattenRoutes, getFirstAccessibleRoute } from "./utils";

/** --------------------------- 路由模块 --------------------------- */
import { staticRouter, errorRouter } from "@/routers/staticRouter";
import tradeManageRouter from "./modules/tradeManage";
import dashboardRouter from "./modules/dashboard";
import userOperationRouter from "./modules/userOperation";
import lianYunUser from "./modules/lianYunUser";
import productManagementRouter from "./modules/productManagement";
import productActivityRouter from "./modules/productActivity";
import supplierManagementRouter from "./modules/supplierManagement";
import systemMangementRouter from "./modules/systemMangement";

import { getBrowserEnv } from "@/utils/global";
import { getQueryString } from "@/utils/wx";
import { loginService } from "@/api/services/loginService";

const pageRoutes = [
    ...dashboardRouter,
    ...productManagementRouter,
    ...productActivityRouter,
    ...tradeManageRouter,
    ...lianYunUser,
    ...userOperationRouter,
    ...supplierManagementRouter,
    ...systemMangementRouter
];

/**
 * @description 📚 路由参数配置简介
 * @param path ==> 路由菜单访问路径
 * @param name ==> 路由 name (对应页面组件 name, 可用作 KeepAlive 缓存标识 && 按钮权限筛选)
 * @param redirect ==> 路由重定向地址
 * @param component ==> 视图文件路径
 * @param meta ==> 路由菜单元信息
 * @param meta.icon ==> 菜单和面包屑对应的图标
 * @param meta.title ==> 路由标题 (用作 document.title || 菜单的名称)
 * @param meta.activeMenu ==> 当前路由为详情页时，需要高亮的菜单
 * @param meta.isLink ==> 路由外链时填写的访问地址
 * @param meta.isHide ==> 是否在菜单中隐藏 (通常列表详情页需要隐藏)
 * @param meta.isFull ==> 菜单是否全屏 (示例：数据大屏页面)
 * @param meta.isAffix ==> 菜单是否固定在标签页中 (首页通常是固定项)
 * @param meta.isKeepAlive ==> 当前路由是否缓存
 * */
const router = createRouter({
    history: createWebHistory(import.meta.env.VITE_PUBLIC_PATH),
    routes: [...staticRouter, ...errorRouter],
    scrollBehavior: () => ({ left: 0, top: 0 })
});

let isDynamicRoutesAdded = false;

/**
 * @description 添加动态路由
 * @param permissions 用户权限列表
 */
const addDynamicRoutes = (permissions: string[]) => {
    if (isDynamicRoutesAdded) {
        return;
    }
    const filteredRoutes = filterRoutes(pageRoutes, permissions);

    filteredRoutes.forEach(route => {
        router.addRoute(route);
    });
    // 保存菜单数据到store
    const userStore = useUserStore();
    userStore.setMenuList(filteredRoutes);
    userStore.setFlatMenuList(flattenRoutes(filteredRoutes));
    isDynamicRoutesAdded = true;
};

/**
 * @description 初始化用户信息和动态路由
 * @param userStore 用户store
 * @returns 用户权限列表
 */
const initializeUserAndRoutes = async (userStore: any) => {
    const data = await loginService.getUserInfo();
    userStore.setUserInfo(data);
    const permissions = userStore.userInfo.permissions || [];
    addDynamicRoutes(permissions);
    return permissions;
};

/**
 * 企业微信-工作台进入。 url中会携带临时code
 */
const wxContextLogin = async () => {
    const userStore = useUserStore();
    // 解析code
    const params = new URL(window.location.href).searchParams;

    const code = params.get("code");
    if (code === null) {
        console.error("缺少code参数");
        return;
    }

    const data = await loginService.enterpriseWeChatLogin({ code, source: "web", url: "/" });
    userStore.setToken(data.accessToken);
};

/**
 * @description 路由拦截 beforeEach
 * */
router.beforeEach(async (to, from) => {
    const userStore = useUserStore();
    const usePathStore = useUserPath();
    // 设置页面路径
    usePathStore.setPath(from.path);
    sessionStorage.setItem("toPath", to.fullPath);

    // 动态设置标题
    document.title = (to.meta.title ? `${to.meta.title?.trim() ?? ""} - 链云砂石` : "链云砂石") as string;

    // 前置检查：企业微信工作台上下文，且携带token参数
    if (getBrowserEnv() === 1) {
        if (!userStore.token) {
            await wxContextLogin();
        }
    }

    // 查看是否有重定向地址
    const redirectUrl = to.query.route;

    let redirectUrlObj = {};

    if (redirectUrl) {
        redirectUrlObj = resolveRedirectUrl(redirectUrl as string);
    }

    const t1 = getQueryString("token");
    // 任意路径， 携带有token参数
    if (t1) {
        userStore.setToken(t1);
    }

    // 判断是访问登陆页，有 Token 就在当前页面，没有 Token 重置路由到登陆页
    if (to.path.toLocaleLowerCase().includes("/login")) {
        if (userStore.token) {
            if (redirectUrl) {
                return {
                    ...redirectUrlObj,
                    replace: true
                };
            }
            return { path: "/", replace: true };
        }

        return true;
    }

    // 判断访问页面是否在路由白名单地址(静态路由)中，如果存在直接放行
    if (ROUTER_WHITE_LIST.includes(to.path)) {
        return true;
    }

    // 判断是否有 Token，没有重定向到 login 页面
    if (!userStore.token) {
        return { path: "/login", replace: true };
    }

    // 如果没有动态路由，就重新请求菜单列表并添加动态路由
    if (!isDynamicRoutesAdded || to.path === "/") {
        const permissions = await initializeUserAndRoutes(userStore);

        // 有重定向路径
        if (redirectUrl) {
            return {
                ...redirectUrlObj,
                replace: true
            };
        }

        // 如果路由在权限里面， 可以解析到， 则直接放行
        if (router.resolve(to.fullPath).matched.length > 0 && to.path !== "/") {
            return {
                path: to.path,
                // replace: true,
                query: to.query
            };
        }

        // 如果路由不存在或解析失败，跳转到第一个有权限的路由
        const firstRoute = getFirstAccessibleRoute(pageRoutes, permissions);
        if (firstRoute) {
            // 如果有children，跳转到第一个子路由
            if (firstRoute.children && firstRoute.children.length > 0) {
                const firstChild = firstRoute.children[0]!;
                return { name: firstChild.name, replace: true };
            }
            return { name: firstRoute.name, replace: true };
        }

        return { path: "/403", replace: true };
    }

    if (to.matched.length > 0) {
        return true;
    } else {
        return {
            path: "/403"
        };
    }
});

/**
 * @description 重置路由
 * */
export const resetRouter = () => {
    const authStore = useAuthStore();
    authStore.flatMenuListGet.forEach(route => {
        const { name } = route;
        if (name && router.hasRoute(name)) router.removeRoute(name);
    });
};

/**
 * @description 路由跳转错误
 * */
router.onError(error => {
    console.warn("路由错误", error);
});

function patchRouter(router: Router) {
    const rawPush = router.push.bind(router);
    const rawReplace = router.replace.bind(router);

    function guard(to: RouteLocationRaw) {
        if (typeof to === "object" && "name" in to && to.name) {
            if (!router.hasRoute(to.name as string)) {
                return { path: "/403" };
            }
        }
        return to;
    }

    try {
        router.push = async to => await rawPush(guard(to));
        router.replace = async to => await rawReplace(guard(to));
    } catch (error) {
        console.error(error);
    }
}

patchRouter(router);

function resolveRedirectUrl(url: string) {
    // 构造一个 URL
    if (!url.startsWith("/")) {
        url = "/" + url;
    }

    const n = new URL(`http://test.com${url}`);

    const query: Record<string, string> = {};

    for (const [key, value] of n.searchParams.entries()) {
        console.log(key, value);
        query[key] = value;
    }

    return {
        path: n.pathname,
        query
    };
}

export default router;
